import { useEffect, useState } from 'react';

/**
 * PWA install helper for the firm panel.
 * - On Chrome/Android/desktop Chrome: captures `beforeinstallprompt` so we can
 *   trigger the native install dialog from our own button.
 * - On iOS Safari there is no such event; installs go via Share -> Add to Home
 *   Screen, so we expose `isIOS` to show instructions instead.
 */
export function usePwaInstall() {
  const [deferred, setDeferred] = useState<any>(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const onPrompt = (e: any) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    const standalone = window.matchMedia?.('(display-mode: standalone)').matches || (navigator as any).standalone === true;
    if (standalone) setInstalled(true);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const isIOS = /iphone|ipad|ipod/i.test(ua) && !(window as any).MSStream;

  const promptInstall = async () => {
    if (!deferred) return false;
    deferred.prompt();
    try { await deferred.userChoice; } catch { /* ignore */ }
    setDeferred(null);
    return true;
  };

  return { canInstall: !!deferred && !installed, installed, isIOS, promptInstall };
}
